import { useSettlements } from '@services/settlements/queries/useSettlements'
import { Receipt } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { FlatDetail } from 'types/api'

import { SectionCard } from '@/features/settlements/components/SectionCard'
import {
  absoluteAmount,
  formatDate,
  isUnderpayment,
  money
} from '@/features/settlements/components/format'
import { cn } from '@/lib/utils'

/**
 * Read-only for the landlord: the periods and their result, no editing and
 * no admin machinery.
 */
export function LandlordFlatSettlements({ flat }: { flat: FlatDetail }) {
  const { t } = useTranslation('settlement')
  const { data, isLoading } = useSettlements({ flat: flat.id })

  return (
    <SectionCard title={t('title')} icon={Receipt}>
      {isLoading ? (
        <div className="h-24 animate-pulse rounded-2xl bg-ink/[0.04]" />
      ) : !data || data.results.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">{t('empty.title')}</p>
      ) : (
        <div className="space-y-2">
          {data.results.map((settlement) => {
            const underpayment = isUnderpayment(settlement.grand_total)

            return (
              <div
                key={settlement.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-ink/[0.08] px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="m-0 font-medium text-ink">{settlement.title}</p>
                  <p className="m-0 text-sm text-muted-foreground">
                    {formatDate(settlement.period_start)} –{' '}
                    {formatDate(settlement.period_end)}
                  </p>
                </div>

                <span className="rounded-full bg-ink/[0.06] px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                  {t(`status.${settlement.status}`)}
                </span>

                {settlement.grand_total !== null && (
                  <span
                    className={cn(
                      'text-sm font-semibold tabular-nums',
                      underpayment ? 'text-destructive' : 'text-success'
                    )}
                  >
                    {underpayment
                      ? t('result.underpayment')
                      : t('result.overpayment')}{' '}
                    {money(absoluteAmount(settlement.grand_total))}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}
